import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { LocationOn as LocationIcon, LocalGasStation as DepotIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const libraries = ['places'];

const DriverMap = () => {
  // Map-related constants (must be declared before useState that uses them)
  const defaultCenter = {
    lat: 47.6062, // Seattle area (adjust to your service area)
    lng: -122.3321,
  };

  const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 200px)',
    minHeight: { xs: '400px', sm: '500px', md: '600px' },
  };

  const [jobs, setJobs] = useState([]);
  const [depots, setDepots] = useState([]);
  const [geocodedDepots, setGeocodedDepots] = useState({});
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [geocodedLocations, setGeocodedLocations] = useState({});
  const geocoderRef = useRef(null);
  const mapRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const locationIntervalRef = useRef(null);
  const initialBoundsSetRef = useRef(false);
  const userHasChangedMapRef = useRef(false); // Track if user manually changed map
  const isUpdatingProgrammaticallyRef = useRef(false); // Track if we're updating programmatically
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);
  
  // Location update state
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationSuccess, setLocationSuccess] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [hasVehicle, setHasVehicle] = useState(false);

  useEffect(() => {
    fetchData();
    // Refresh jobs every 30 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    // Refresh driver location every 10 seconds (matches update frequency)
    const fetchDriverLocation = async () => {
      try {
        const profileResponse = await api.get('/drivers/profile/').catch(() => null);
        
        if (profileResponse?.data) {
          // Get vehicle ID from assigned vehicle - check both current_vehicle (UUID) and current_vehicle_info.id
          const vehicleId = profileResponse.data.current_vehicle || 
                           profileResponse.data.current_vehicle_info?.id;
          
          // Update hasVehicle state
          setHasVehicle(!!vehicleId);
          
          if (vehicleId) {
            try {
              const vehicleResponse = await api.get(`/vehicles/${vehicleId}/`);
              const vehicle = vehicleResponse.data;
              
              // If vehicle has GPS coordinates, update location
              if (vehicle.latitude != null && vehicle.longitude != null) {
                setDriverLocation((prevLocation) => {
                  const newLocation = {
                    lat: parseFloat(vehicle.latitude),
                    lng: parseFloat(vehicle.longitude),
                    vehicle: vehicle,
                  };
                  
                  // Only update if location actually changed
                  if (!prevLocation || 
                      prevLocation.lat !== newLocation.lat || 
                      prevLocation.lng !== newLocation.lng) {
                    return newLocation;
                  }
                  return prevLocation;
                });
              } else {
                // Vehicle exists but has no location
                setDriverLocation(null);
              }
            } catch (vehicleError) {
              console.error('Error fetching vehicle location:', vehicleError);
              // If vehicle fetch fails (404), clear hasVehicle state
              if (vehicleError.response?.status === 404) {
                setHasVehicle(false);
                setDriverLocation(null);
              }
            }
          } else {
            // No vehicle assigned
            setHasVehicle(false);
            setDriverLocation(null);
          }
        }
      } catch (error) {
        console.error('Error fetching driver location:', error);
      }
    };

    // Fetch driver location immediately, then every 10 seconds
    fetchDriverLocation();
    locationIntervalRef.current = setInterval(fetchDriverLocation, 10000);

    // Fallback timeout to hide loading if map doesn't load within 15 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      setMapLoading((prevLoading) => {
        if (prevLoading) {
          console.warn('Map loading timeout - hiding loading overlay');
          setMapLoaded((prevLoaded) => {
            if (!prevLoaded) {
              setError('Map is taking longer than expected to load. Please try refreshing the page.');
            }
            return prevLoaded;
          });
          return false;
        }
        return prevLoading;
      });
    }, 15000);

    return () => {
      clearInterval(interval);
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      // Geocode job locations even if there are no jobs (to handle future jobs)
      if (jobs.length > 0) {
        geocodeJobLocations(jobs);
      }
    }
  }, [mapLoaded, jobs]);

  useEffect(() => {
    if (mapLoaded && depots.length > 0 && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocodeDepotLocations(depots);
    }
  }, [mapLoaded, depots]);

  // Handle case where script loads but map doesn't load
  useEffect(() => {
    if (scriptLoaded && !mapLoaded) {
      // If script is loaded but map hasn't loaded after 8 seconds, hide loading overlay
      const timeout = setTimeout(() => {
        setMapLoaded((prevLoaded) => {
          if (!prevLoaded) {
            setMapLoading((prevLoading) => {
              if (prevLoading) {
                console.warn('Script loaded but map did not load - hiding loading overlay');
                return false;
              }
              return prevLoading;
            });
          }
          return prevLoaded;
        });
      }, 8000);
      
      return () => clearTimeout(timeout);
    }
  }, [scriptLoaded, mapLoaded]);

  const fetchData = async () => {
    try {
      setError('');
      setDataLoading(true);

      // Fetch driver profile, active jobs, and fuel depots in parallel
      const [profileResponse, jobsResponse, depotsResponse] = await Promise.all([
        api.get('/drivers/profile/').catch(() => null),
        api.get('/drivers/jobs_active/').catch(() => null),
        api.get('/depots/').catch(() => ({ data: [] })),
      ]);

      const depotsData = Array.isArray(depotsResponse.data)
        ? depotsResponse.data
        : depotsResponse.data?.results || [];
      setDepots(depotsData);

      // Handle driver profile and location
      if (profileResponse?.data) {
        setDriverProfile(profileResponse.data);
        
        // Get vehicle ID from assigned vehicle - check both current_vehicle (UUID) and current_vehicle_info.id
        const vehicleId = profileResponse.data.current_vehicle || 
                         profileResponse.data.current_vehicle_info?.id;
        
        // Update hasVehicle state
        setHasVehicle(!!vehicleId);
        
        if (vehicleId) {
          try {
            const vehicleResponse = await api.get(`/vehicles/${vehicleId}/`);
            const vehicle = vehicleResponse.data;
            
            // If vehicle has GPS coordinates, use them
            if (vehicle.latitude != null && vehicle.longitude != null) {
              setDriverLocation({
                lat: parseFloat(vehicle.latitude),
                lng: parseFloat(vehicle.longitude),
                vehicle: vehicle,
              });
            } else {
              // If vehicle exists but has no location, clear driver location
              setDriverLocation(null);
            }
          } catch (vehicleError) {
            console.error('Error fetching vehicle location:', vehicleError);
            // If vehicle fetch fails, clear hasVehicle state
            if (vehicleError.response?.status === 404) {
              setHasVehicle(false);
              setDriverLocation(null);
            }
          }
        } else {
          // No vehicle assigned
          setHasVehicle(false);
          setDriverLocation(null);
        }
      }

      // Handle jobs
      if (jobsResponse?.data) {
        const jobsData = Array.isArray(jobsResponse.data)
          ? jobsResponse.data
          : [];

        // Filter out completed jobs
        const activeJobsData = jobsData.filter(
          (job) => job.job_status !== 'complete' && job.job_status !== 'completed'
        );

        setJobs(activeJobsData);
      } else {
        // Try alternative endpoint if driver-specific endpoint fails
        try {
          const altResponse = await api.get('/jobs/active/');
          const altJobsData = Array.isArray(altResponse.data)
            ? altResponse.data
            : [];
          const activeJobsData = altJobsData.filter(
            (job) => job.job_status !== 'complete' && job.job_status !== 'completed'
          );
          setJobs(activeJobsData);
        } catch (altError) {
          console.error('Error with alternative endpoint:', altError);
          if (!jobsResponse) {
            setError('Failed to load active jobs');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load map data');
    } finally {
      setDataLoading(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser. Please use a modern browser with GPS support.'));
        return;
      }

      // Check if we're in a secure context (HTTPS required for geolocation on mobile)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        reject(new Error('GPS location requires HTTPS. Please access the app via a secure connection.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Verify we got valid coordinates
          if (position.coords.latitude == null || position.coords.longitude == null) {
            reject(new Error('Invalid GPS coordinates received. Please ensure location services are enabled.'));
            return;
          }
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          // Provide user-friendly error messages based on error code
          let errorMessage = 'Failed to get GPS location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Location permission denied. Please enable location access in your browser settings and allow location access for this site.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'GPS position unavailable. Please ensure location services are enabled on your device and you have a clear view of the sky.';
              break;
            case error.TIMEOUT:
              errorMessage += 'GPS request timed out. Please try again. Make sure you are outdoors or near a window for better GPS signal.';
              break;
            default:
              errorMessage += `Error: ${error.message || 'Unknown error'}. Please check your device GPS settings.`;
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true, // Use GPS on mobile devices
          timeout: 15000, // Increased timeout for mobile GPS (15 seconds)
          maximumAge: 10000, // Accept cached position up to 10 seconds old (mobile-friendly)
        }
      );
    });
  };

  const updateLocation = async () => {
    if (!hasVehicle) {
      setLocationError('No vehicle assigned. Please assign a vehicle first.');
      return;
    }

    setUpdatingLocation(true);
    setLocationError('');
    setLocationSuccess('');

    try {
      const location = await getCurrentLocation();
      setCurrentLocation(location);

      const response = await api.post('/drivers/update_location/', {
        latitude: location.latitude,
        longitude: location.longitude,
      });

      setLocationSuccess('Location updated successfully!');
      setLocationError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setLocationSuccess(''), 3000);
      
      // Refresh driver location on map
      fetchData();
    } catch (error) {
      console.error('Error updating location:', error);
      if (error.message) {
        setLocationError(error.message);
      } else if (error.response?.data?.error) {
        setLocationError(error.response.data.error);
      } else if (error.response?.data?.detail) {
        setLocationError(error.response.data.detail);
      } else {
        setLocationError('Failed to update location. Please try again.');
      }
    } finally {
      setUpdatingLocation(false);
    }
  };

  // Automatically start location tracking when vehicle is assigned
  useEffect(() => {
    if (hasVehicle && !locationIntervalRef.current) {
      // Start tracking automatically
      // Update immediately
      updateLocation();

      // Then update every 10 seconds
      locationIntervalRef.current = setInterval(() => {
        updateLocation();
      }, 10000); // 10 seconds
    } else if (!hasVehicle && locationIntervalRef.current) {
      // Stop tracking if vehicle is unassigned
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
      setLocationSuccess('Location tracking stopped.');
    }
    
    // Cleanup on unmount or when hasVehicle changes
    return () => {
      if (!hasVehicle && locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVehicle]);

  const geocodeJobLocations = async (jobsData) => {
    if (!geocoderRef.current || !window.google) return;

    const geocoder = geocoderRef.current;
    const locations = {};

    for (const job of jobsData) {
      // Use get_full_delivery_address if available, otherwise construct from address fields
      let address = '';
      if (job.job_location) {
        address = job.job_location;
      } else if (job.get_full_delivery_address) {
        address = job.get_full_delivery_address;
      } else if (job.delivery_address) {
        const parts = [
          job.delivery_address,
          job.delivery_city,
          job.delivery_state,
          job.delivery_zip,
        ].filter(Boolean);
        address = parts.join(', ');
      }

      if (address && !geocodedLocations[job.id]) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0].geometry.location);
              } else {
                reject(status);
              }
            });
          });

          locations[job.id] = {
            lat: result.lat(),
            lng: result.lng(),
          };
        } catch (err) {
          console.error(`Failed to geocode ${address}:`, err);
        }
      }
    }

    if (Object.keys(locations).length > 0) {
      setGeocodedLocations((prev) => ({ ...prev, ...locations }));
    }
  };

  const geocodeDepotLocations = async (depotsData) => {
    if (!geocoderRef.current || !window.google || !depotsData?.length) return;
    const geocoder = geocoderRef.current;
    const additions = {};
    for (const d of depotsData) {
      if (d.latitude != null && d.longitude != null) continue;
      const addr = d.full_address;
      if (!addr) continue;
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: addr }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0].geometry.location);
            } else {
              reject(status);
            }
          });
        });
        additions[d.id] = {
          lat: result.lat(),
          lng: result.lng(),
        };
      } catch (err) {
        console.warn(`Failed to geocode depot ${addr}:`, err);
      }
    }
    if (Object.keys(additions).length > 0) {
      setGeocodedDepots((prev) => ({ ...prev, ...additions }));
    }
  };

  const getDepotPosition = (d) => {
    if (d.latitude != null && d.longitude != null) {
      return { lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) };
    }
    return geocodedDepots[d.id] || null;
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google) {
      setMapLoaded(true);
      setMapLoading(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }
  }, []);

  const getJobStatusColor = (status) => {
    const statusColors = {
      pending: '#ff9800',
      assigned: '#2196f3',
      in_progress: '#9c27b0',
      en_route: '#00bcd4',
      complete: '#4caf50',
      cancelled: '#f44336',
    };
    return statusColors[status] || '#757575';
  };

  const getJobStatusLabel = (status) => {
    const statusLabels = {
      pending: 'Pending',
      assigned: 'Assigned',
      in_progress: 'In Progress',
      en_route: 'En Route',
      complete: 'Complete',
      cancelled: 'Cancelled',
    };
    return statusLabels[status] || status;
  };

  const getMarkers = () => {
    const markers = [];

    // Add driver location marker if available
    if (driverLocation && driverLocation.lat && driverLocation.lng) {
      markers.push({
        id: 'driver-location',
        type: 'driver',
        position: {
          lat: driverLocation.lat,
          lng: driverLocation.lng,
        },
        title: 'Your Location',
        vehicle: driverLocation.vehicle,
      });
    }

    // Add job markers
    const jobMarkers = jobs
      .filter((job) => {
        const location = geocodedLocations[job.id];
        return location && location.lat && location.lng;
      })
      .map((job) => {
        const location = geocodedLocations[job.id];
        const address =
          job.job_location ||
          job.get_full_delivery_address ||
          `${job.delivery_address || ''}, ${job.delivery_city || ''}, ${job.delivery_state || ''} ${job.delivery_zip || ''}`.trim();

        return {
          id: job.id,
          type: 'job',
          position: location,
          title: `Job ${job.id?.substring(0, 8) || 'N/A'}`,
          job: job,
          address: address,
        };
      });

    const depotMarkers = depots
      .map((d) => {
        const pos = getDepotPosition(d);
        if (!pos) return null;
        return {
          id: `depot-${d.id}`,
          type: 'depot',
          position: pos,
          title: d.name,
          depot: d,
        };
      })
      .filter(Boolean);

    return [...markers, ...jobMarkers, ...depotMarkers];
  };

  const getMarkerIcon = (type) => {
    if (!window.google?.maps) return null;

    if (type === 'depot') {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#F9A825',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }

    if (type === 'driver') {
      // Truck icon - side view style but from top: rectangular body with cab
      // Creates a recognizable truck shape with cab (front) and cargo area (back)
      return {
        path: 'M-15,-7 L-8,-7 L-8,-4 L8,-4 L8,-7 L15,-7 L15,7 L-15,7 Z M-12,-5 L-9,-5 L-9,-2 L-12,-2 Z M9,-5 L12,-5 L12,5 L9,5 Z',
        fillColor: '#1E3A5F',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2.5,
        scale: 1.5,
        anchor: new window.google.maps.Point(0, 0),
        rotation: 0,
      };
    } else if (type === 'job') {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#2196f3',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }
    return null;
  };

  // Calculate map bounds to fit all markers
  const getMapBounds = () => {
    const markers = getMarkers();
    if (markers.length === 0) return null;

    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => {
      bounds.extend(marker.position);
    });
    return bounds;
  };

  // Get center point for map - prioritize driver location, fallback to default
  const getMapCenter = () => {
    if (driverLocation && driverLocation.lat && driverLocation.lng) {
      return {
        lat: driverLocation.lat,
        lng: driverLocation.lng,
      };
    }
    return defaultCenter;
  };

  // Set initial map bounds/center only once when map first loads with data
  // Never change center/zoom if user has manually changed the map
  useEffect(() => {
    if (mapRef.current && mapLoaded && !initialBoundsSetRef.current && !userHasChangedMapRef.current) {
      const markers = getMarkers();
      
      // Set flag to prevent callbacks from firing during programmatic update
      isUpdatingProgrammaticallyRef.current = true;
      
      if (markers.length > 0) {
        // If we have markers, fit bounds to show all of them (only on initial load)
        const bounds = getMapBounds();
        if (bounds) {
          mapRef.current.fitBounds(bounds);
          // Update state with current center/zoom after fitBounds
          const center = mapRef.current.getCenter();
          if (center) {
            setMapCenter({ lat: center.lat(), lng: center.lng() });
          }
          setMapZoom(mapRef.current.getZoom());
          initialBoundsSetRef.current = true;
        }
      } else if (driverLocation && driverLocation.lat && driverLocation.lng) {
        // If no markers but we have driver location, center on driver (only on initial load)
        const center = { lat: driverLocation.lat, lng: driverLocation.lng };
        mapRef.current.setCenter(center);
        mapRef.current.setZoom(15); // Zoom in on driver location
        setMapCenter(center);
        setMapZoom(15);
        initialBoundsSetRef.current = true;
      } else {
        // If no markers and no driver location, use default center (already set)
        setMapCenter(defaultCenter);
        setMapZoom(10);
        initialBoundsSetRef.current = true;
      }
      
      // Reset flag after a short delay to allow map to update
      setTimeout(() => {
        isUpdatingProgrammaticallyRef.current = false;
      }, 100);
    }
  }, [mapLoaded, geocodedLocations, geocodedDepots, jobs, depots, driverLocation]);

  // Don't automatically pan/zoom when driver location updates - let user control the map
  // Only update the marker position, not the map view

  return (
    <Layout>
      <Container maxWidth={false} sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h4" gutterBottom>
              Active Jobs Map
            </Typography>
            {driverLocation && (
              <Chip
                icon={<LocationIcon />}
                label="Your Location"
                color="primary"
                variant="outlined"
                sx={{ mb: 2 }}
              />
            )}
            {depots.length > 0 && (
              <Chip
                icon={<DepotIcon />}
                label={`${depots.length} fuel depot${depots.length === 1 ? '' : 's'}`}
                color="warning"
                variant="outlined"
                sx={{ mb: 2 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            View active jobs, nearby fuel depots, and your current location on the map
          </Typography>

          {/* Location Update Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon />
                  Vehicle Location
                </Typography>
              </Box>

              {!hasVehicle && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No vehicle assigned. Please contact a manager to assign a vehicle.
                </Alert>
              )}

              {locationError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocationError('')}>
                  {locationError}
                </Alert>
              )}

              {locationSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLocationSuccess('')}>
                  {locationSuccess}
                </Alert>
              )}

              {currentLocation && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Current: ${currentLocation.latitude.toFixed(6)}, ${currentLocation.longitude.toFixed(6)}`}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<LocationIcon />}
                  onClick={updateLocation}
                  disabled={!hasVehicle || updatingLocation}
                >
                  {updatingLocation ? 'Updating...' : 'Update Location Now'}
                </Button>

                {locationIntervalRef.current && (
                  <Chip
                    label="Tracking Active"
                    color="success"
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {hasVehicle ? (
                  locationIntervalRef.current ? (
                    "Location tracking is active. Your location is automatically updated every 10 seconds."
                  ) : (
                    "Location tracking will start automatically when a vehicle is assigned."
                  )
                ) : (
                  "Assign a vehicle to enable automatic location tracking."
                )}
                {" "}Click "Update Location Now" for a one-time manual update.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {dataLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        )}

        {mapLoading && !scriptError && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              background: 'rgba(255, 255, 255, 0.9)',
              p: 3,
              borderRadius: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading map...
            </Typography>
          </Box>
        )}

        <Paper sx={{ m: 2, position: 'relative' }}>
          <LoadScript
            googleMapsApiKey={
              process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
              'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E'
            }
            libraries={libraries}
            onLoad={() => {
              setScriptLoaded(true);
              // If script loads but map doesn't load within a reasonable time, hide loading
              setTimeout(() => {
                if (mapLoading && !mapLoaded) {
                  console.warn('Map script loaded but map component did not load - hiding loading overlay');
                  setMapLoading(false);
                }
              }, 5000);
            }}
            onError={() => {
              setScriptError(true);
              setMapLoading(false);
              setError(
                'Failed to load Google Maps script. Please check your API key.'
              );
            }}
          >
            {scriptError ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
                p={3}
              >
                <Alert severity="error">
                  Failed to load Google Maps. Please check your API key and
                  ensure the Maps JavaScript API is enabled in Google Cloud
                  Console.
                </Alert>
              </Box>
            ) : scriptLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter || (driverLocation && driverLocation.lat && driverLocation.lng
                  ? { lat: driverLocation.lat, lng: driverLocation.lng }
                  : defaultCenter)}
                zoom={mapZoom || (driverLocation && driverLocation.lat && driverLocation.lng ? 15 : 10)}
                onLoad={onMapLoad}
                onCenterChanged={() => {
                  // Ignore if we're updating programmatically
                  if (isUpdatingProgrammaticallyRef.current) {
                    return;
                  }
                  // Only track user changes, don't update state (prevents infinite loop)
                  if (mapRef.current && initialBoundsSetRef.current) {
                    userHasChangedMapRef.current = true;
                  }
                }}
                onZoomChanged={() => {
                  // Ignore if we're updating programmatically
                  if (isUpdatingProgrammaticallyRef.current) {
                    return;
                  }
                  // Only track user changes, don't update state (prevents infinite loop)
                  if (mapRef.current && initialBoundsSetRef.current) {
                    userHasChangedMapRef.current = true;
                  }
                }}
                onError={(error) => {
                  console.error('GoogleMap error:', error);
                  setMapLoading(false);
                  setMapLoaded(false);
                  setError('Failed to load Google Map. Please try refreshing the page.');
                  if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                  }
                }}
                options={{
                  disableDefaultUI: false,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
                {getMarkers().map((marker) => (
                  <Marker
                    key={marker.id}
                    position={marker.position}
                    title={marker.title}
                    icon={getMarkerIcon(marker.type)}
                    onClick={() => setSelectedMarker(marker)}
                  />
                ))}

                {selectedMarker && (
                  <InfoWindow
                    position={
                      selectedMarker.type === 'driver' && driverLocation
                        ? { lat: driverLocation.lat, lng: driverLocation.lng }
                        : selectedMarker.position
                    }
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <Box sx={{ p: 1, minWidth: '200px' }}>
                      {selectedMarker.type === 'driver' ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Your Location
                          </Typography>
                          {selectedMarker.vehicle && (
                            <>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Vehicle:</strong>{' '}
                                {selectedMarker.vehicle.make}{' '}
                                {selectedMarker.vehicle.model}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>License Plate:</strong>{' '}
                                {selectedMarker.vehicle.license_plate}
                              </Typography>
                              {selectedMarker.vehicle.location_updated_at && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Last Updated:</strong>{' '}
                                  {new Date(
                                    selectedMarker.vehicle.location_updated_at
                                  ).toLocaleString()}
                                </Typography>
                              )}
                            </>
                          )}
                        </>
                      ) : selectedMarker.type === 'depot' ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Fuel depot
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{selectedMarker.depot.name}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Operator:</strong> {selectedMarker.depot.parent_company}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {selectedMarker.depot.full_address}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Fuels:</strong>{' '}
                            {[selectedMarker.depot.has_diesel && 'Diesel', selectedMarker.depot.has_def && 'DEF']
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                          {(selectedMarker.depot.contact_phone || selectedMarker.depot.contact_email) && (
                            <Typography variant="body2">
                              <strong>Contact:</strong>{' '}
                              {[selectedMarker.depot.contact_name, selectedMarker.depot.contact_phone, selectedMarker.depot.contact_email]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Job Details
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Job ID:</strong>{' '}
                            {selectedMarker.job.id?.substring(0, 8) || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Status:</strong>{' '}
                            <Chip
                              label={getJobStatusLabel(
                                selectedMarker.job.job_status
                              )}
                              size="small"
                              sx={{
                                backgroundColor: getJobStatusColor(
                                  selectedMarker.job.job_status
                                ),
                                color: '#ffffff',
                              }}
                            />
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Location:</strong> {selectedMarker.address}
                          </Typography>
                          {selectedMarker.job.diesel_quantity && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Diesel:</strong>{' '}
                              {selectedMarker.job.diesel_quantity} gal
                            </Typography>
                          )}
                          {selectedMarker.job.def_quantity && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>DEF:</strong>{' '}
                              {selectedMarker.job.def_quantity} gal
                            </Typography>
                          )}
                          {selectedMarker.job.customer_name && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Customer:</strong>{' '}
                              {selectedMarker.job.customer_name}
                            </Typography>
                          )}
                          {selectedMarker.job.driver_name && (
                            <Typography variant="body2">
                              <strong>Driver:</strong>{' '}
                              {selectedMarker.job.driver_name}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
              >
                <CircularProgress />
              </Box>
            )}
          </LoadScript>
        </Paper>

        {!dataLoading && jobs.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">
              No active jobs found. All jobs are either completed or cancelled.
            </Alert>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default DriverMap;

